import {
  adminV1OrganizationsTeamsList,
  adminV1OrganizationsTeamsCreate,
  adminV1OrganizationsTeamsDestroy,
  adminV1OrganizationsTeamsRetrieve,
  adminV1OrganizationsTeamsUpdate,
} from "@/api/admin-teams/admin-teams";
import {
  adminV1OrganizationsProfilesTeamsCreate,
  adminV1OrganizationsProfilesTeamsDestroy,
} from "@/api/admin-profiles/admin-profiles";

import { adminV1OrganizationsCoachesList } from "@/api/admin-coaches/admin-coaches";
import type { AdminTeam } from "@/api/model";
import { EVENT_NAMES, addMixpanelEvent } from "../../shared/utils/analytics";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";

const getDefaultOrganizationId = (): number => {
  const { currentOrganizationId } = useCurrentOrganization();
  return currentOrganizationId.value || 0;
};

export const getTeams = async (
  resultsPerPage: number,
  paginationNum: number,
  organization_id?: number
) => {
  try {
    const organizationId = organization_id || getDefaultOrganizationId();
    const result = await adminV1OrganizationsTeamsList(organizationId, {
      limit: resultsPerPage,
      offset: paginationNum,
    });
    return result;
  } catch (e: any) {
    return { error: e?.message };
  }
};

export const getTeam = async (teamId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsTeamsRetrieve(
      organizationId as number,
      teamId
    );
    return result;
  } catch (e: any) {
    return { error: e?.message };
  }
};

export const updateTeam = async (
  teamId: number,
  form: Pick<AdminTeam, "name" | "organization_id">
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsTeamsUpdate(
      organizationId,
      teamId,
      form
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const createTeam = async (form: Pick<AdminTeam, "name">) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsTeamsCreate(organizationId, {
      name: form?.name,
    });
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteTeam = async (teamId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsTeamsDestroy(organizationId, teamId);
  } catch (e: any) {
    return { error: e.message };
  }
};

export const addUserToTeam = async (teamId: number, userId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesTeamsCreate(
      organizationId,
      userId,
      teamId
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteUserFromTeam = async (userId: number, teamId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesTeamsDestroy(
      organizationId,
      userId,
      teamId
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const deleteMemberFromTeam = async (userId: number, teamId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesTeamsDestroy(
      organizationId,
      userId,
      teamId
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getCoaches = async (
  teamIds: number[],
  limit: number,
  offset: number
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const coaches =
      (await adminV1OrganizationsCoachesList(organizationId, {
        teams: teamIds,
        limit: limit,
        offset: offset,
      })) || [];
    return coaches;
  } catch (e: any) {
    return {
      error: e?.message,
    };
  }
};
